import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useSearch } from "../context/SearchContext";
import "../styles/SearchBar.css";

const SearchBar = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef(null);

  // Always call useSearch, but handle errors gracefully
  const searchContext = useSearch();

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (searchTerm.trim() && !isSearching) {
      setShowSuggestions(false);
      setIsSearching(true);
      setHasError(false);
      try {
        await searchContext.search(searchTerm.trim());
        navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      } catch (error) {
        console.error("Search error:", error);
        setHasError(true);
      } finally {
        setIsSearching(false);
      }
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(e.target.value.length > 0);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setShowSuggestions(false);
    setHasError(false);
    inputRef.current?.focus();
  };

  if (hasError) {
    return (
      <div className="nav-search error">
        <div className="search-input-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            placeholder="Search failed. Try again..."
            className="search-input error"
            autoComplete="off"
          />
          <button
            type="button"
            className="clear-button"
            onClick={clearSearch}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="nav-search" ref={inputRef}>
      <form onSubmit={handleSubmit}>
        <div className="search-input-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={() => setShowSuggestions(searchTerm.length > 0)}
            placeholder="Search cheatsheets..."
            className="search-input"
            autoComplete="off"
            disabled={isSearching}
          />
          {searchTerm && (
            <button
              type="button"
              className="clear-button"
              onClick={clearSearch}
              aria-label="Clear search"
              disabled={isSearching}
            >
              <FaTimes />
            </button>
          )}
        </div>
      </form>

      {showSuggestions && searchTerm && (
        <div className="suggestions-dropdown">
          <div className="suggestion-item">
            {isSearching
              ? "Searching..."
              : `Press Enter to search for "${searchTerm}"`}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
